import React, { useCallback, useState } from 'react'
import type { Brief } from '@disco/types/types/brief'
import { saveBrief } from '@disco/state-management/reducers/entities/briefs'
import { pushUINotification } from '@disco/utils/notifications'
import { checkDateIsInPast, checkTimeIsInPast } from '@disco/utils/time-utils'
import { useDispatch } from 'hooks/use-redux'
import { EmailSchedulerModal } from 'components/modals/email-scheduler'
import {
  formatScheduledCampaignData,
  getTimeDateToScheduleEmail,
} from '@disco/utils/emailCampaigns'
import type { ScheduledValue } from '@disco/types/types/campaign'

export type UseReopenBriefOptions = {
  brief: Brief | null
  onSuccess?: (updatedBrief: Brief) => void
  onCancel?: () => void
}

/**
 * Hook for handling brief reopening logic with deadline picker modal
 * @param options - Configuration options for reopening the brief
 * @returns Object containing reopening state, handler function, and deadline picker modal component
 */
export const useReopenBrief = ({
  brief,
  onSuccess,
  onCancel,
}: UseReopenBriefOptions) => {
  const dispatch = useDispatch()
  const [reopening, setReopening] = useState(false)
  const [isReopenDeadlinePickerOpen, setIsReopenDeadlinePickerOpen] =
    useState(false)

  const isDeadlineInPast =
    brief?.submission_status === 'closed' &&
    !!brief?.deadline &&
    !!brief?.deadline_timezone &&
    (checkDateIsInPast(brief.deadline, brief.deadline_timezone) ||
      checkTimeIsInPast(brief.deadline, new Date().toISOString()))

  const handleSetReopenDeadline = useCallback(
    async ({
      date,
      time,
      timeZone,
    }: {
      date: string
      time: ScheduledValue
      timeZone: ScheduledValue
    }) => {
      if (!brief) return

      const deadlineToScheduleEmail = getTimeDateToScheduleEmail(
        date as any,
        time.value,
        timeZone.value,
      )

      try {
        const updatedBrief = await dispatch(
          saveBrief({
            ...brief,
            deadline: deadlineToScheduleEmail,
            deadline_timezone: timeZone.value,
            submission_status: 'open',
          }),
        ).unwrap()

        setIsReopenDeadlinePickerOpen(false)
        onSuccess?.(updatedBrief)

        pushUINotification({
          message: 'Brief has been reopened successfully',
          level: 'success',
        })
      } catch (error) {
        pushUINotification({
          message: 'Failed to reopen brief',
          level: 'error',
        })
      }
    },
    [brief, onSuccess, dispatch],
  )

  const handleReopenBrief = useCallback(async (): Promise<void> => {
    if (!brief) {
      return
    }

    if (isDeadlineInPast) {
      // Show deadline picker modal to require user to set a new future deadline
      setIsReopenDeadlinePickerOpen(true)
      return
    }

    // If no deadline or deadline is in the future, proceed with reopening
    setReopening(true)
    try {
      const updatedBrief = await dispatch(
        saveBrief({ ...brief, submission_status: 'open' }),
      ).unwrap()

      onSuccess?.(updatedBrief)

      pushUINotification({
        message: 'Brief has been reopened successfully',
        level: 'success',
      })
    } catch (error) {
      pushUINotification({
        message: 'Failed to reopen brief',
        level: 'error',
      })
    } finally {
      setReopening(false)
    }
  }, [brief, isDeadlineInPast, onSuccess, dispatch])

  const campaignSchedule = isReopenDeadlinePickerOpen
    ? formatScheduledCampaignData({
        scheduled_at: brief?.deadline,
        scheduled_at_timezone: brief?.deadline_timezone,
        status: 'draft',
      })
    : null

  const deadlinePickerModal = isReopenDeadlinePickerOpen ? (
    <EmailSchedulerModal
      onClose={() => {
        setIsReopenDeadlinePickerOpen(false)
        onCancel?.()
      }}
      setScheduleData={handleSetReopenDeadline}
      storedCampaignSchedule={campaignSchedule}
      caption="Brief Deadline"
      description="Pick a date, time and timezone for your submissions deadline. Once the deadline has passed submissions for this brief will be closed."
      primaryButtonCaption="Set and reopen"
      errorExplanation="To reopen this brief, please set a deadline in the future."
      showInlineErrors={false}
      showCloseButton={false}
    />
  ) : null

  return {
    reopening,
    isDeadlineInPast,
    handleReopenBrief,
    deadlinePickerModal,
  }
}
